import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { CareElementFrequencyType } from "@/Flowsheets/Models/CareElementFrequencyType";
import { LastDateReviewedDatePicker } from "../LastDateReviewedDatePicker";
import { SnapshotDates } from "./SnapshotDates";

import styles from "./CareElementDateReviewed.module.scss";

interface ICareElementDateReviewedProps {
    id: string;
    isEditable: boolean;
    dataCy: string;
    encounterDate: Date;
    state: {
        Name: string;
        Value?: string | number | boolean | string[] | Date | null;
        DateReviewed?: Date | null;
        DateReviewedWithPrecision?: DateTimeWithPrecision | null;
        LastReviewedDate?: Date | null;
        Frequency?: CareElementFrequencyType;
    };
    handleChange?: (newState: DateTimeWithPrecision | null) => void;
    setFalseAsValid?: boolean;
    isDatePickerVisible?: boolean;
}

/**
 * CareElementDateReviewed component for rendering a care element date reviewed section.
 * @param props - The properties for the component.
 * @property {string} props.id - The unique identifier for the component.
 * @property {boolean} props.isEditable - Indicates if the care element is editable.
 * @property {string} props.dataCy - The data attribute for testing purposes.
 * @property {Date} props.encounterDate - The date of the encounter.
 * @property {object} props.state - The state of the care element.
 * @property {function} [props.handleChange] - Callback function to handle changes to the date reviewed.
 * @property {boolean} [props.setFalseAsValid] - Indicates if false values should be considered valid.
 * @returns The rendered component.
 */
export function CareElementDateReviewed({
    id,
    isEditable,
    dataCy,
    encounterDate,
    state,
    handleChange,
    setFalseAsValid,
    isDatePickerVisible
}: Readonly<ICareElementDateReviewedProps>): JSX.Element {
    let showDatePicker = false;

    const careElementValue = state.Value;

    if (isDatePickerVisible !== null && isDatePickerVisible !== undefined) {
        showDatePicker = isDatePickerVisible;
    }
    else {
        if (typeof careElementValue === "boolean") {
            showDatePicker =
                careElementValue === true ||
                (setFalseAsValid && careElementValue === false);
        }
        else if (typeof careElementValue === "number") {
            showDatePicker = !Number.isNaN(careElementValue);
        }
        else if (typeof careElementValue === "string") {
            showDatePicker = !IsNullOrWhiteSpace(careElementValue);
        }
        else if (careElementValue instanceof Date) {
            showDatePicker = !Number.isNaN(careElementValue.getTime());
        }

        if (Array.isArray(state.Value) && state.Value.length > 0) {
            showDatePicker = true;
        }
    }

    return (
        <div className={`${styles.dateReviewedContainer}`}>
            {isEditable ? (
                <div className={styles.datePickerWrapper}>
                    <LastDateReviewedDatePicker
                        id={id}
                        dataCy={dataCy}
                        showDatePicker={showDatePicker}
                        encounterDate={encounterDate}
                        dateValue={state.DateReviewedWithPrecision ?? null}
                        lastReviewedDate={state.LastReviewedDate ?? null}
                        onChange={handleChange}
                    />
                </div>
            ) : (
                <SnapshotDates
                    dateReviewed={state.DateReviewed}
                    lastReviewedDate={state.LastReviewedDate}
                />
            )
            }
        </div>
    );
}
